import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Grid, Card, Icon, Chip, Badge, Modal, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useAuth } from "../../../AuthContext";
import { COLORS } from "theme/tokens";

function BankSkillPage() {
  const { token } = useAuth();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSkill, setSelectedSkill] = useState(null);

  const fetchSkills = useCallback(async () => {
    try {
      const res = await axios.get("/api/admin/bank-factory/skills", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSkills(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("獲取技能失敗", err);
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDelete = useCallback(async (id, name) => {
    if (!window.confirm(`確定要刪除技能「${name}」嗎？`)) return;
    try {
      await axios.delete(`/api/admin/bank-factory/skills/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSkills();
    } catch (err) {
      alert("刪除失敗：" + (err?.response?.data?.error || err.message));
    }
  }, [token, fetchSkills]);

  useEffect(() => {
    if (token) fetchSkills();
  }, [token, fetchSkills]);

  const columns = React.useMemo(() => [
    { 
      Header: "技能名稱", 
      accessor: "name",
      Cell: ({ value, row }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          {row.original.isUniversal && (
            <Icon sx={{ color: "#fb8c00", fontSize: "1.2rem !important" }}>stars</Icon>
          )}
          <MDTypography variant="button" fontWeight="bold">
            {value}
          </MDTypography>
        </MDBox>
      )
    },
    { Header: "類別", accessor: "category", Cell: ({ value }) => (
      <Chip label={value || "未分類"} size="small" sx={{ bgcolor: "#e3f2fd", color: "#1976d2", fontWeight: "bold" }} />
    )},
    { 
      Header: "權威認證 (Source)", 
      accessor: "sourceCount",
      Cell: ({ value }) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          <Badge badgeContent={value || 0} color="info" showZero>
            <Icon color="secondary">groups</Icon>
          </Badge>
          <MDTypography variant="caption" color="text" ml={1}>
            {value >= 3 ? "宗師級" : "精英級"}
          </MDTypography>
        </MDBox>
      )
    },
    { 
      Header: "適用範圍", 
      accessor: "isUniversal",
      Cell: ({ value, row }) => (
        value ? (
          <Chip label="全域通用" color="warning" variant="gradient" size="small" />
        ) : (
          <MDTypography variant="caption" color="text">
            {row.original.applicableSymbols?.length || 0} 個標的
          </MDTypography>
        )
      )
    },
    {
      Header: "操作",
      accessor: "actions",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <MDButton variant="text" color="info" onClick={() => setSelectedSkill(row.original)}>
            查看邏輯
          </MDButton>
          <MDButton variant="text" color="error" onClick={() => handleDelete(row.original.id, row.original.name)}>
            <Icon>delete</Icon>
          </MDButton>
        </MDBox>
      ),
    },
  ], [handleDelete]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold">
                    🎖️ Beast AI 宗師技能館
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    系統自動萃取、精煉並傳承的交易智慧庫
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="info" onClick={fetchSkills}>
                  <Icon>refresh</Icon>
                </MDButton>
              </MDBox>
              <MDBox pt={1}>
                {loading ? (
                  <MDBox p={5} textAlign="center"><CircularProgress /></MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows: skills || [] }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* 邏輯詳情彈窗 */}
      <Modal open={!!selectedSkill} onClose={() => setSelectedSkill(null)}>
        <MDBox sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 800, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
          maxHeight: '90vh', overflowY: 'auto'
        }}>
          <MDTypography variant="h5" fontWeight="bold" mb={2}>
            {selectedSkill?.name} - 核心邏輯
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={3} sx={{ whiteSpace: 'pre-wrap', bgcolor: COLORS.grayLightest, p: 2, borderRadius: 1 }}>
            {selectedSkill?.logicDescription}
          </MDTypography>
          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Python 代碼片段：
          </MDTypography>
          <MDBox component="pre" sx={{ bgcolor: "#263238", color: "#eeffff", p: 2, borderRadius: 1, fontSize: "0.85rem", overflowX: 'auto' }}>
            {selectedSkill?.codeSnippet}
          </MDBox>
          <MDBox mt={3} textAlign="right">
            <MDButton onClick={() => setSelectedSkill(null)} color="secondary">關閉</MDButton>
          </MDBox>
        </MDBox>
      </Modal>
    </DashboardLayout>
  );
}

export default BankSkillPage;
